import FormControl from "@mui/material/FormControl";

import { useFormik } from "formik";
import * as yup from "yup";

import { Country, State } from "country-state-city";
import {
  postcodeValidator,
  postcodeValidatorExistsForCountry,
} from "postcode-validator";
import { dateSuffix } from "@utils/date";
import { ProjectLineItem } from "@interfaces/project";
import {
  Box,
  Container,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { getLongitudeAndLatitude } from "@hooks/utils/useLocation";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { SubTitle } from "@stories/atoms/SubTitle/SubTitle";

interface ProjectFormProps {
  project: ProjectLineItem;
  handleNext: () => void;
  setProject: (project: ProjectLineItem) => void;
}

export const ProjectForm = ({
  project,
  setProject,
  handleNext,
}: ProjectFormProps) => {
  const validationSchema = yup.object({
    projectName: yup.string().required("Project name is required"),
    addressLine1: yup.string().required("Address Line 1 is required"),
    addressLine2: yup.string(),
    country: yup.string().required("Country is required"),
    state: yup.string().required("State is required"),
    city: yup.string().required("City is required"),
    postCode: yup
      .string()
      .required("Post code is required")
      .test(
        "Valid for country",
        "Not a valid post code for country",
        function (item) {
          if (postcodeValidatorExistsForCountry(this.parent.country)) {
            const valid = postcodeValidator(item, this.parent.country);
            return valid;
          }
          return true;
        }
      ),
    startDate: yup.date().required("Start date due date is required"),
    endDate: yup
      .date()
      .required("Completion is required")
      .test(
        "After start date",
        "Completion date must be after start date",
        function (item) {
          if (this.parent.startDate) {
            return this.parent.startDate < item;
          }
          return true;
        }
      ),
    reportingDueDate: yup
      .number()
      .integer()
      .typeError("Must be a number between 1 and 28")
      .min(1, "Must be more than 0")
      .max(28, "Must be less than 29")
      .required("Reporting due date is required"),
  });
  const formik = useFormik({
    initialValues: {
      projectName: project.projectName,
      addressLine1: project.projectAddressLine1,
      addressLine2: project.projectAddressLine2,
      country: project.projectCountry,
      state: project.projectState,
      city: project.projectCity,
      postCode: project.projectPostCode,
      startDate: dayjs(project.startDate),
      endDate: dayjs(project.endDate),
      reportingDueDate: project.reportingDueDate,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const lngLat = await getLongitudeAndLatitude(
          values.country,
          values.state,
          values.postCode,
          ""
        );

        setProject({
          ...project,
          projectName: values.projectName,
          projectAddressLine1: values.addressLine1,
          projectAddressLine2: values.addressLine2,
          projectCountry: values.country,
          projectState: values.state,
          projectCity: values.city,
          projectPostCode: values.postCode,
          geoLocation: lngLat,
          startDate: values.startDate.toDate(),
          endDate: values.endDate.toDate(),
          reportingDueDate: values.reportingDueDate,
        });
        handleNext();
      } catch (err: any) {
        console.log(err);
        alert("Something went wrong, please try again or contact support");
        formik.setSubmitting(false);
      }
    },
  });

  const countries = Country.getAllCountries();
  const states = State.getStatesOfCountry(formik.values.country);
  return (
    <StyledCard>
      <Grid container spacing={2} sx={{ padding: "5%" }}>
        <Grid item xs={12} md={12}>
          <Grid item xs={12} md={12}>
            <SubTitle title={"Project Details"} />
          </Grid>
          <Box>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12}>
                  <TextField
                    id="projectName"
                    name="projectName"
                    label="Project name"
                    fullWidth
                    margin="dense"
                    disabled={formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.projectName}
                    error={
                      formik.touched.projectName &&
                      Boolean(formik.errors.projectName)
                    }
                    helperText={
                      formik.touched.projectName
                        ? formik.errors.projectName
                        : ""
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="addressLine1"
                    name="addressLine1"
                    label="Address Line 1"
                    fullWidth
                    margin="dense"
                    disabled={formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.addressLine1}
                    error={
                      formik.touched.addressLine1 &&
                      Boolean(formik.errors.addressLine1)
                    }
                    helperText={
                      formik.touched.addressLine1
                        ? formik.errors.addressLine1
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="addressLine2"
                    name="addressLine2"
                    label="Address Line 2"
                    fullWidth
                    margin="dense"
                    disabled={formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.addressLine2}
                    error={
                      formik.touched.addressLine2 &&
                      Boolean(formik.errors.addressLine2)
                    }
                    helperText={
                      formik.touched.addressLine2
                        ? formik.errors.addressLine2
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <StyledTextField
                      select
                      id="country"
                      name="country"
                      fullWidth
                      margin="dense"
                      disabled={formik.isSubmitting}
                      value={formik.values.country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.country && Boolean(formik.errors.country)
                      }
                      helperText={
                        formik.touched.country ? formik.errors.country : ""
                      }
                      label="Country"
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.isoCode} value={c.isoCode}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <StyledTextField
                      select
                      id="state"
                      name="state"
                      fullWidth
                      margin="dense"
                      disabled={formik.isSubmitting}
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.state && Boolean(formik.errors.state)
                      }
                      helperText={
                        formik.touched.state ? formik.errors.state : ""
                      }
                      label="State"
                    >
                      {states.map((s, index) => (
                        <MenuItem
                          key={`${s.isoCode}-${index}`}
                          value={s.isoCode}
                        >
                          {s.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <StyledTextField
                      id="city"
                      name="city"
                      fullWidth
                      margin="dense"
                      disabled={formik.isSubmitting}
                      value={formik.values.city}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.city && Boolean(formik.errors.city)}
                      helperText={formik.touched.city ? formik.errors.city : ""}
                      label="City"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="postCode"
                    name="postCode"
                    label="Postcode"
                    fullWidth
                    margin="dense"
                    disabled={formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.postCode}
                    error={
                      formik.touched.postCode && Boolean(formik.errors.postCode)
                    }
                    helperText={
                      formik.touched.postCode ? formik.errors.postCode : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Project Start Date"
                    onChange={(value) => {
                      if (value) {
                        formik.setFieldValue("startDate", value, true);
                      }
                    }}
                    format="DD-MM-YYYY"
                    disabled={formik.isSubmitting}
                    value={formik.values.startDate}
                    maxDate={dayjs(project.firstPackageStartDate)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText:
                          formik.touched.startDate &&
                          Boolean(formik.errors.startDate)
                            ? "Invalid date"
                            : "",
                        error:
                          formik.touched.startDate &&
                          Boolean(formik.errors.startDate),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Project Completion Date"
                    onChange={(value) => {
                      if (value) {
                        formik.setFieldValue("endDate", value, true);
                      }
                    }}
                    format="DD-MM-YYYY"
                    disabled={formik.isSubmitting}
                    value={formik.values.endDate}
                    minDate={dayjs(project.lastPackageEndDate)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText:
                          formik.touched.endDate &&
                          Boolean(formik.errors.endDate)
                            ? "Invalid date"
                            : "",
                        error:
                          formik.touched.endDate &&
                          Boolean(formik.errors.endDate),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    id="reportingDueDate"
                    name="reportingDueDate"
                    label="Reporting Due Date"
                    fullWidth
                    margin="dense"
                    disabled={formik.isSubmitting}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.reportingDueDate}
                    error={
                      formik.touched.reportingDueDate &&
                      Boolean(formik.errors.reportingDueDate)
                    }
                    helperText={
                      formik.touched.reportingDueDate
                        ? formik.errors.reportingDueDate
                        : "Day of the month"
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {formik.values.reportingDueDate
                            ? dateSuffix(formik.values.reportingDueDate)
                            : ""}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid container mt={1}>
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    sx={{
                      display: "flex !important",
                      justifyContent: "left !important;",
                    }}
                  ></Grid>
                  <Grid
                    item
                    xs={6}
                    sm={6}
                    sx={{
                      display: "flex !important",
                      justifyContent: "right !important;",
                    }}
                  >
                    <StyledButton
                      variant="contained"
                      type="submit"
                      disabled={formik.isSubmitting}
                    >
                      Next
                    </StyledButton>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
